const fieldResults = {
  MATCH: { label: 'Match', tone: 'success' },
  NO_MATCH: { label: 'Not a match', tone: 'error' },
  CONDITIONAL_MATCH: { label: 'Conditional match', tone: 'warning' },
  NOT_AVAILABLE: { label: 'Unavailable', tone: 'neutral' }
};

const outcomes = {
  FULL_MATCH: { label: 'Full match', tone: 'success' },
  PARTIAL_MATCH: { label: 'Partial match', tone: 'warning' },
  OWNERSHIP_UNAVAILABLE: { label: 'Ownership unavailable', tone: 'warning' },
  NOT_FOUND: { label: 'Account not found', tone: 'error' },
  ERROR: { label: 'Check failed', tone: 'error' }
};

export const avsFieldResult = result =>
  fieldResults[result] || fieldResults.NOT_AVAILABLE;

export const avsOutcome = outcome =>
  outcomes[outcome] || { label: 'Unavailable', tone: 'neutral' };

export const formatAvsDate = value => {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime())
    ? date.toLocaleString()
    : 'Unavailable';
};

export const isAvsDataStale = (prefill, now = Date.now()) => {
  if (!prefill || prefill.verification_is_stale) return true;
  const refreshed = Date.parse(prefill.verification_last_refreshed);
  return (
    !Number.isFinite(refreshed) ||
    refreshed > now ||
    now - refreshed > 24 * 60 * 60 * 1000
  );
};

export const avsCarrierDataWarning = (
  carrierokRefreshed,
  isDummy,
  carrierDataMatches,
  prefill,
  now = Date.now()
) => {
  if (!Number.isFinite(carrierokRefreshed)) {
    return 'CarrierOK verification data is not available yet. The check will use the values in the form.';
  }
  if (isDummy) {
    return 'CarrierOK is showing sample data. The check will use the values in the form.';
  }
  if (!carrierDataMatches) {
    return 'CarrierOK and AVS data are out of sync. Refresh to load the latest CarrierOK data, or run the check with the values in the form.';
  }
  if (isAvsDataStale(prefill, now)) {
    return 'CarrierOK data is more than 24 hours old. Refresh CarrierOK for current data, or run the check with the values in the form.';
  }
  return '';
};

export const avsFreshnessResult = (carrierokRefreshed, stale) => ({
  label: !Number.isFinite(carrierokRefreshed)
    ? 'Verification unavailable'
    : stale
    ? 'Refresh needed'
    : 'Data current',
  tone: !Number.isFinite(carrierokRefreshed) || stale ? 'warning' : 'success'
});

export const avsFormValues = (prefill = {}, edits = {}) => {
  const address = prefill.physical_address || {};
  return {
    business_name: prefill.business_name || '',
    tax_id: prefill.tax_id || '',
    routing_number: prefill.routing_number || '',
    street: address.street || '',
    city: address.city || '',
    state: address.state || '',
    zip_code: address.zip_code || '',
    ...edits
  };
};

export const avsRunDisabledReason = (prefill, values) => {
  if (!prefill) return 'Load verification data before running an AVS check.';
  if (!prefill.has_account) {
    return 'Add a default ACH account in Payment Methods before running AVS.';
  }
  if (!values.business_name.trim()) return 'Enter the company name to check.';
  if (prefill.tax_id && !values.tax_id.trim()) {
    return 'Enter the existing or replacement Tax ID. AVS cannot clear a stored Tax ID.';
  }
  if (!/^\d{9}$/.test(values.routing_number.trim())) {
    return 'Enter a nine-digit routing number.';
  }
  return '';
};

export const avsRequestOverrides = values => ({
  business_name: values.business_name.trim(),
  tax_id: values.tax_id.trim(),
  routing_number: values.routing_number.trim(),
  physical_address: {
    street: values.street.trim(),
    city: values.city.trim(),
    state: values.state.trim(),
    zip_code: values.zip_code.trim()
  }
});

export const avsErrorMessage = error => {
  const response = error && error.response;
  if (response && response.status === 503) {
    return 'AVS is currently disabled. Contact your administrator to enable it.';
  }
  if (response && (response.status === 401 || response.status === 403)) {
    return 'You do not have permission to access AVS for this payment profile.';
  }
  if (response && response.status === 404) {
    return 'AVS is not available for this payment profile.';
  }
  return 'Unable to complete the AVS request. Please try again.';
};

export const avsHistoryRows = check => {
  return [
    ['Company name', check.business_name, check.result_business_name],
    ['Tax ID', check.tax_id, check.result_tax_id],
    ['Physical address', check.physical_address_street, check.result_address],
    ['City', check.physical_address_city, check.result_city],
    ['State', check.physical_address_state, check.result_state],
    ['Zip code', check.physical_address_zip, check.result_zip]
  ];
};

export const avsProviderDetails = check => {
  const details = [
    ['Status code', check.provider_status_code],
    ['Status description', check.provider_status_description],
    ['Condition code', check.provider_condition_code],
    ['Match score', check.provider_match_score]
  ];

  return details.filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  );
};
